import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { extractApiErrorMessage } from '@/lib/api-client';
import {
  useIssueInvoiceMutation,
  type IssueInvoiceResponse,
} from '../api/use-issue-invoice-mutation';
import { invoiceActionAvailable } from '../lib/invoice-actions';
import { issueActionCopy, type IssueActionCopy } from '../lib/issue-action-copy';
import { invoiceTypeLabel } from '../lib/invoice-label';
import type { InvoiceRow } from '../types';

const ISSUER_MISMATCH_TOAST =
  'Invoice issuer mismatch detected. Approval required from CFO and Management.';

export interface IssueInvoiceConfirmationProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly invoice: InvoiceRow;
  readonly onIssued?: (() => void) | undefined;
}

interface ConfirmButtonProps {
  readonly idleLabel: string;
  readonly pendingLabel: string;
  readonly isPending: boolean;
  readonly onConfirm: () => void;
}

function IssueDialogHeader({
  copy,
  label,
}: {
  readonly copy: IssueActionCopy;
  readonly label: string;
}) {
  return (
    <AlertDialogHeader>
      <AlertDialogTitle>
        {copy.present} this {label}?
      </AlertDialogTitle>
      <AlertDialogDescription>
        Once {copy.pastAdjective}, fields are locked. To correct, void and recreate, or raise an
        Adjustment Note.
      </AlertDialogDescription>
    </AlertDialogHeader>
  );
}

function ConfirmIssueButton({ idleLabel, pendingLabel, isPending, onConfirm }: ConfirmButtonProps) {
  return (
    <AlertDialogAction asChild>
      <Button
        type="button"
        variant="destructive"
        disabled={isPending}
        data-testid="issue-invoice-confirm"
        onClick={(e) => {
          e.preventDefault();
          onConfirm();
        }}
      >
        {isPending ? (
          <>
            <Loader2 className="size-4 animate-spin" aria-hidden />
            {pendingLabel}
          </>
        ) : (
          idleLabel
        )}
      </Button>
    </AlertDialogAction>
  );
}

export function IssueInvoiceConfirmation(props: IssueInvoiceConfirmationProps) {
  const { open, onOpenChange, invoice, onIssued } = props;
  const label = invoiceTypeLabel(invoice);
  const copy = issueActionCopy(invoice);
  const mutation = useIssueInvoiceMutation({
    invoiceId: invoice.id,
    orderId: invoice.order_id ?? '',
    blOrderId: invoice.bl_order_id ?? undefined,
  });
  const handleSuccess = (data: IssueInvoiceResponse) => {
    if (data.requiresApproval) toast.warning(ISSUER_MISMATCH_TOAST);
    else toast.success(`Invoice ${copy.pastAdjective}.`);
    onIssued?.();
    onOpenChange(false);
  };
  const handleError = (err: Error) => {
    toast.error(
      extractApiErrorMessage(err, `Failed to ${copy.present.toLowerCase()} the ${label}.`),
    );
  };
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // Don't let an outside-click / Escape dismiss the dialog mid-issue —
        // the generation round-trip takes a few seconds and the spinner must stay visible.
        if (!mutation.isPending) onOpenChange(next);
      }}
    >
      <AlertDialogContent className="max-w-sm sm:max-w-lg">
        <IssueDialogHeader copy={copy} label={label} />
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button type="button" variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </AlertDialogCancel>
          <ConfirmIssueButton
            idleLabel={`${copy.present} ${label}`}
            pendingLabel={`${copy.gerund}…`}
            isPending={mutation.isPending}
            onConfirm={() => {
              mutation.mutate(undefined, { onSuccess: handleSuccess, onError: handleError });
            }}
          />
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export interface IssueButtonProps {
  readonly invoice: InvoiceRow;
  readonly onIssued?: (() => void) | undefined;
  readonly compact?: boolean | undefined;
}

export function IssueButton(props: IssueButtonProps) {
  const { invoice, onIssued, compact } = props;
  const [open, setOpen] = useState(false);
  if (!invoiceActionAvailable(invoice, 'issue')) return null;
  const copy = issueActionCopy(invoice);
  return (
    <>
      <Button
        type="button"
        variant="default"
        size="sm"
        className={compact ? 'h-7 gap-1 px-3 text-xs' : undefined}
        data-testid={`invoice-${invoice.id}-issue`}
        onClick={() => {
          setOpen(true);
        }}
      >
        {copy.trigger}
      </Button>
      <IssueInvoiceConfirmation
        open={open}
        onOpenChange={setOpen}
        invoice={invoice}
        onIssued={onIssued}
      />
    </>
  );
}
